<script setup lang="ts">
import { cn } from '@/lib/utils'
import {
  SliderRange,
  SliderRoot,
  type SliderRootEmits,
  type SliderRootProps,
  SliderThumb,
  SliderTrack,
  useForwardPropsEmits,
} from 'radix-vue'
import { computed, type HTMLAttributes } from 'vue'

const props = defineProps<SliderRootProps & {
  class?: HTMLAttributes['class']
  /** Accessible name per thumb, in value order. */
  thumbLabels?: string[]
  /** Human-readable value per thumb (aria-valuetext), e.g. a timecode. */
  thumbValueTexts?: string[]
}>()

const emits = defineEmits<SliderRootEmits>()

const delegatedProps = computed(() => {
  const { class: _, thumbLabels: __, thumbValueTexts: ___, ...delegated } = props

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <SliderRoot
    :class="cn(
      'relative flex w-full touch-none select-none items-center data-[disabled]:opacity-50',
      props.class,
    )"
    v-bind="forwarded"
  >
    <!-- bg-input, not bg-secondary: secondary equals the muted panels the slider sits on, hiding the unselected track. -->
    <SliderTrack class="relative h-2 w-full grow overflow-hidden rounded-full bg-input">
      <SliderRange class="absolute h-full bg-primary" />
    </SliderTrack>
    <SliderThumb
      v-for="(_, index) in (modelValue || defaultValue || [])"
      :key="index"
      :aria-label="thumbLabels?.[index]"
      :aria-valuetext="thumbValueTexts?.[index]"
      class="block h-5 w-5 rounded-full border-2 border-primary bg-background shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none [@media(pointer:coarse)]:h-6 [@media(pointer:coarse)]:w-6"
    />
  </SliderRoot>
</template>
